import { computeHalfTrimWidth } from '../../helpers/page-trim-width'
import { GAP_WARNING } from '../constants/bleed-copy'

type SpineGutterOverlayProps = {
  bleedHeight: number
  bleedMargin: number
  trimHeight: number
  trimWidth: number
  centerGap: number
  visible?: boolean
  /**
   * Stacking within the spread frame. Default 8 — above inner-trim bleed (z-7); below layout grid (z-10).
   */
  zIndexBase?: number
}

/**
 * Bindery gutter warning label only — no fill (gutter yellow is in `PrintBleedTints`; see
 * `centerGap`) and no dashed trim bridge (the two page-half `TrimFrame`s already draw a single
 * continuous dashed line across the full spread width, gutter included, so a separate bridge here
 * duplicated it with a mismatched dash phase).
 */
export function SpineGutterOverlay({
  bleedHeight: _bleedHeight,
  bleedMargin,
  trimHeight,
  trimWidth,
  centerGap,
  visible = true,
  zIndexBase = 8,
}: SpineGutterOverlayProps) {
  const showGutter = visible && centerGap > 0 && trimWidth > 0

  if (!showGutter) {
    return null
  }

  const pageTrimWidth = computeHalfTrimWidth(trimWidth)
  const spineX = bleedMargin + pageTrimWidth

  return (
    <>
      {(
        [
          { rotate: '90deg' as const, xNudge: -Math.max(1, Math.floor(centerGap / 4)) },
          { rotate: '-90deg' as const, xNudge: Math.max(1, Math.floor(centerGap / 4)) },
        ] as const
      ).map(({ rotate, xNudge }, idx) => (
        <div
          key={`gap-warning-${idx}`}
          style={{
            position: 'absolute',
            left: spineX + xNudge,
            top: bleedMargin + trimHeight / 2,
            transform: `translate(-50%, -50%) rotate(${rotate})`,
            transformOrigin: 'center center',
            color: 'var(--doc-label)',
            fontSize: 8,
            lineHeight: '10px',
            userSelect: 'none',
            textAlign: 'center',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            zIndex: zIndexBase + 2,
          }}
          aria-hidden="true"
        >
          {GAP_WARNING}
        </div>
      ))}
    </>
  )
}
